// packages/web-ui/src/state/tabs-persistence.ts
//
// W2-S4 AC #3 — `localStorage`-backed persistence for the tab list. Single
// fixed key (per AC #3 + Pitfall #19) — per-workspace fingerprinting is
// deferred to W3-S6 once the SPA learns the workspace path from
// `/api/v1/system/capabilities`.
//
// TODO(W3-S6): per-workspace key
//
// Writes are debounced at 100 ms via a single shared setTimeout/clearTimeout
// pair to avoid thrash on rapid drag-reorder. JS-spec localStorage writes are
// atomic so no torn-state mitigation is needed (the debounce is purely a
// performance optimisation per AC #3).

import type { ActiveDestination } from './navigation.js';

/** Storage key. Single fixed namespace — per-workspace key lands in W3-S6. */
export const TABS_STORAGE_KEY = 'tessa-lean.layout.tabs';

/** Debounce window (ms) before flushing the latest tabs snapshot to storage. */
export const TABS_PERSIST_DEBOUNCE_MS = 100;

/** Persisted shape per AC #3 — `isDirty` is intentionally NOT persisted. */
export interface PersistedTab {
  id: string;
  destination: ActiveDestination;
  resourceId: string | null;
  label: string;
  pinned: boolean;
}

export interface PersistedTabsState {
  openTabs: PersistedTab[];
  activeTabId: string | null;
  tabOrder: string[];
  pinnedTabIds: string[];
}

let pendingTimer: ReturnType<typeof setTimeout> | null = null;
let pendingSnapshot: PersistedTabsState | null = null;

/**
 * Reads the persisted tabs snapshot from `localStorage`. Returns `null` when
 * storage is unavailable, the key is missing, or the JSON is unparseable.
 */
export function loadTabsFromStorage(): PersistedTabsState | null {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = window.localStorage.getItem(TABS_STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!isPersistedTabsState(parsed)) {
      console.warn('[tessa-lean/web-ui] Failed to restore tabs: unexpected shape');
      return null;
    }
    return parsed;
  } catch (err) {
    console.warn('[tessa-lean/web-ui] Failed to restore tabs', err);
    return null;
  }
}

/**
 * Synchronously writes a snapshot. Test-only: production callers should go
 * through `scheduleTabsPersistFlush` for the debounce.
 */
export function saveTabsToStorage(state: PersistedTabsState): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    window.localStorage.setItem(TABS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage may be full / disabled — silently ignore. The in-memory state
    // is still correct; only persistence is lost.
  }
}

/**
 * Schedules a debounced write. Multiple calls within 100 ms coalesce into
 * a single `localStorage.setItem`. The latest snapshot wins.
 */
export function scheduleTabsPersistFlush(snapshot: PersistedTabsState): void {
  pendingSnapshot = snapshot;
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
  }
  pendingTimer = setTimeout(() => {
    if (pendingSnapshot !== null) {
      saveTabsToStorage(pendingSnapshot);
    }
    pendingTimer = null;
    pendingSnapshot = null;
  }, TABS_PERSIST_DEBOUNCE_MS);
}

/** Test-only — synchronously flushes any pending debounced write. */
export function _flushTabsPersistForTests(): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  if (pendingSnapshot !== null) {
    saveTabsToStorage(pendingSnapshot);
    pendingSnapshot = null;
  }
}

/** Test-only — drops any pending debounced write without flushing. */
export function _resetTabsPersistForTests(): void {
  if (pendingTimer !== null) {
    clearTimeout(pendingTimer);
    pendingTimer = null;
  }
  pendingSnapshot = null;
}

function isPersistedTabsState(value: unknown): value is PersistedTabsState {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.openTabs)) return false;
  if (!Array.isArray(v.tabOrder)) return false;
  if (!Array.isArray(v.pinnedTabIds)) return false;
  if (v.activeTabId !== null && typeof v.activeTabId !== 'string') return false;
  for (const t of v.openTabs as unknown[]) {
    if (typeof t !== 'object' || t === null) return false;
    const tab = t as Record<string, unknown>;
    if (typeof tab.id !== 'string') return false;
    if (typeof tab.destination !== 'string') return false;
    if (tab.resourceId !== null && typeof tab.resourceId !== 'string') return false;
    if (typeof tab.label !== 'string') return false;
    if (typeof tab.pinned !== 'boolean') return false;
  }
  for (const id of v.tabOrder as unknown[]) {
    if (typeof id !== 'string') return false;
  }
  for (const id of v.pinnedTabIds as unknown[]) {
    if (typeof id !== 'string') return false;
  }
  return true;
}
